import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";

// Components
import { Layout } from "@/components/common/layout";
import { ClientAccess, AccountSettings } from "@/pages/client-access";
import { Loop } from "@/pages/loop";

// Pages
import {
  Login,
  Dashboard,
  Sessions,
  Orphans,
  Upload,
  CameraPerformance,
  StateAnalytics,
  UserGuide,
  Misreads,
  CameraAudits,
} from "@/pages";

// Hooks and Providers
import { useAuthContext, AuthContext } from "@/hooks/use-auth";
import { useToastContext, ToastContext } from "@/hooks/use-toast";
import { DateRangeProvider } from "@/contexts/DateRangeContext";

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = React.useContext(AuthContext)!;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Main App Providers
function AppProviders({ children }: { children: React.ReactNode }) {
  const authContext = useAuthContext();
  const toastContext = useToastContext();

  // Create QueryClient
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={authContext}>
        <ToastContext.Provider value={toastContext}>
          <DateRangeProvider>{children}</DateRangeProvider>
        </ToastContext.Provider>
      </AuthContext.Provider>
    </QueryClientProvider>
  );
}

// Toast Notifications Component
function ToastNotifications() {
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: "hsl(var(--card))",
          color: "hsl(var(--card-foreground))",
          border: "1px solid hsl(var(--border))",
        },
        success: {
          iconTheme: {
            primary: "hsl(var(--primary))",
            secondary: "hsl(var(--primary-foreground))",
          },
        },
        error: {
          iconTheme: {
            primary: "hsl(var(--destructive))",
            secondary: "hsl(var(--destructive-foreground))",
          },
        },
      }}
    />
  );
}

// Main App Component
function App() {
  return (
    <AppProviders>
      <Router>
        <div className="min-h-screen bg-background font-sans antialiased">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      {/* Redirect root to dashboard */}
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />

                      {/* Main Application Routes */}
                      <Route path="/access" element={<ClientAccess />} />
                      <Route path="/account" element={<AccountSettings />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/loop/*" element={<Loop />} />
                      <Route path="/sessions" element={<Sessions />} />
                      <Route path="/orphans" element={<Orphans />} />
                      <Route path="/upload" element={<Upload />} />
                      <Route
                        path="/state-analytics"
                        element={<StateAnalytics />}
                      />
                      <Route path="/misreads" element={<Misreads />} />
                      <Route path="/camera-audits" element={<CameraAudits />} />
                      <Route path="/user-guide" element={<UserGuide />} />

                      {/* Catch all - redirect to dashboard */}
                      <Route
                        path="*"
                        element={<Navigate to="/dashboard" replace />}
                      />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>

        {/* Global Toast Notifications */}
        <ToastNotifications />
      </Router>
    </AppProviders>
  );
}

export default App;
